import * as React from 'react'
import * as Redux from 'react-redux'
import { styled } from 'next-yak'

import * as model from '@owl-nest/models'
import * as shadow from '@owl-nest/shadow'
import * as plume from '@ulule/owl-kit-components/next'

import { Project } from '../../../../models/project'
import { RootState } from '../../../../../reducers'
import { useNavigation } from '../../../../hooks/useNavigation'
import { PATHS } from '../../../../paths'

import { SponsorshipTag } from '../SponsorshipTag'

type Sponsor = {
  avatar: string | undefined
  mode: string
  name: string
  ownerId: number
  tag: React.ReactNode | undefined
}

type SponsorPropsType = {
  className?: string
}

function SponsorsComponent({ className }: SponsorPropsType): React.ReactElement<SponsorPropsType> {
  const project = Redux.useSelector<RootState, Project>((state) => state.application.project.data)

  const [indexActive, setIndexActive] = React.useState(0)
  const sponsors = (project.sponsorships || []).map((sponsorship) => {
    return {
      avatar: model.sponsorship.avatar(sponsorship, 'small'),
      mode: model.sponsorship.mode(sponsorship),
      name: sponsorship.sponsor.name,
      ownerId: sponsorship.sponsor.user.id,
      tag: <SponsorshipTag project={project} sponsorship={sponsorship} />,
    }
  })

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const newIndex = indexActive >= sponsors.length - 1 ? 0 : indexActive + 1
      setIndexActive(newIndex)
    }, 5000)
    return () => clearTimeout(timer)
  }, [indexActive])

  return (
    <InfoCardWrapper className={className}>
      <InfoCard>
        {sponsors.map((sponsor, index) => (
          <AnimatedCard active={indexActive === index} key={index}>
            <Sponsor index={index} sponsor={sponsor} />
          </AnimatedCard>
        ))}
      </InfoCard>
    </InfoCardWrapper>
  )
}

export const Sponsors = styled(SponsorsComponent)``

type SponsorProps = {
  index: number
  sponsor: Sponsor
}

export function Sponsor({ index, sponsor }: SponsorProps): React.ReactElement<SponsorProps> {
  const project = Redux.useSelector<RootState, Project>((state) => state.application.project.data)
  const navigation = useNavigation()
  const sponsorRef = React.useRef(null)

  shadow.usePartnershipTracker(sponsor.ownerId, sponsorRef, {
    click: {
      enabled: true,
    },
    eventPosition: `sponsorship-banner-${index + 1}`,
    ignoreLocationChange: true,
    impression: {
      enabled: true,
      once: true,
    },
  })

  return (
    <SponsorWrapper ref={sponsorRef}>
      <plume.styles.copy.S.regular>{sponsor.mode}</plume.styles.copy.S.regular>
      <InfoCardItem
        onClick={() => {
          const path = document.location.pathname
          if (path !== project.absolute_url) {
            navigation.to(PATHS.PROJECT, {
              scrollTarget: 'sponsors',
            })
          } else {
            document.location.hash = 'sponsors'
          }
        }}
      >
        {sponsor.avatar && <plume.styles.image.Avatar src={sponsor.avatar} type="extra-small" />}
        <InfoCardContent>
          <plume.styles.heading.XXXS>{sponsor.name}</plume.styles.heading.XXXS>
          {sponsor.tag}
        </InfoCardContent>
      </InfoCardItem>
    </SponsorWrapper>
  )
}

const AnimatedCard = styled.div<{ active?: boolean }>`
  display: ${({ active }) => (active ? 'flex' : 'none')};
  opacity: ${({ active }) => (active ? '1' : '0')};
  z-index: ${({ active }) => (active ? '1' : '-1')};
  transition: 1s ease;
  top: 0;
  left: 0;
`

const InfoCard = styled.div`
  position: relative;

  ${plume.styles.tag.Tag} {
    margin-top: 4px;

    @media screen and ${plume.BREAKPOINTS.LAPTOP} {
      margin-left: 10px;
    }
  }
`

const InfoCardWrapper = styled.div`
  svg {
    color: ${plume.COLORS.PRIMARY_SAND_500};
    margin-right: 1px;
  }
`

const SponsorWrapper = styled.div`
  align-items: center;
  display: flex;

  ${plume.styles.copy.S.regular} {
    color: ${plume.COLORS.TEXT_SECONDARY};
    margin-right: 12px;
  }
`

const InfoCardContent = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    right: 0;
    top: 20px;
    flex-direction: row;
  }
`

const InfoCardItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;

  ${plume.styles.image.Avatar} {
    background-color: ${plume.COLORS.PRIMARY_GREY_000};
    margin-right: 12px;
  }
`
